import React from 'react';
import { useAppUser } from 'App/components/utils/providers/AppUserProvider';
import { useAppLogger } from 'Services/logger';
import { usesSession } from 'Services/api/auth/base';
import { createRemindersApi } from './taskSdk';

const initialFetchDelayMs = 1000;
const refreshIntervalMs = 60000;

type ReminderUser = Pick<ReturnType<typeof useAppUser>, 'sub' | 'token'>;

/** With a session the SDK sends the cookie; without one this account's own token goes by hand, as before. */
export const reminderCountParams = ( user?: ReminderUser ): { authorization?: string; filters: string } | undefined => {
  const token = user?.token.current;
  if ( !user?.sub || !token ) {
    return undefined;
  }
  const filters = `userSub==${user.sub}`;
  if ( usesSession( token.authMode ) ) {
    return { filters };
  }

  return token.value ? { authorization: `Bearer ${token.value}`, filters } : undefined;
};

export const useReminderCount = (): number => {
  const user = useAppUser();
  const logger = useAppLogger();
  const [ amount, setAmount ] = React.useState<number>( 0 );

  const remindersApi = React.useMemo( () => createRemindersApi(), [] );

  React.useEffect( () => {
    let intervalId: number | undefined;
    let timeoutId: number | undefined;
    let isMounted = true;

    const fetchCount = async (): Promise<void> => {
      const params = reminderCountParams( user );
      if ( !params ) {
        return;
      }

      try {
        const response = await remindersApi.getReminderCounts( params );

        if ( isMounted ) {
          const data = response.data;
          setAmount( ( data.dueCount ?? 0 ) + ( data.overdueCount ?? 0 ) );
        }
      } catch ( error ) {
        logger.error( error );
      }
    };

    timeoutId = window.setTimeout( fetchCount, initialFetchDelayMs );
    intervalId = window.setInterval( fetchCount, refreshIntervalMs );

    return () => {
      isMounted = false;
      window.clearTimeout( timeoutId );
      window.clearInterval( intervalId );
    };
  }, [ logger, remindersApi, user ] );

  return amount;
};
